import {textblockTypeInputRule} from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Heading from '@tiptap/extension-heading';
import {HorizontalRule} from '@tiptap/extension-horizontal-rule';
import {BulletList} from '@tiptap/extension-bullet-list';
import {OrderedList} from '@tiptap/extension-ordered-list';
import {Blockquote} from '@tiptap/extension-blockquote';
import {Strike} from '@tiptap/extension-strike';
import {Code} from '@tiptap/extension-code';

import {TipTapFormats} from 'common/components/tiptap-editor/constants';

import {StyledLink} from './styled-link-extension';

enum KEYBOARD_OVERRIDES {
  CmdShiftS = `Cmd-Shift-s`, // strikethrough
  CmdAlt1 = `Cmd-Alt-1`, // override h1 to nothing (ctrl alt 1 / mod alt 1)
  CmdAlt2 = `Cmd-Alt-2`, // title (ctrl alt 2 / mod alt 2)
  CmdAlt3 = `Cmd-Alt-3`, // subtitle (ctrl alt 3 / mod alt 3)
  CmdAlt4 = `Cmd-Alt-4`, // bulleted list (ctrl alt 4 / mod alt 4)
  CmdAlt5 = `Cmd-Alt-5`, // numbered list (ctrl alt 5 / mod alt 5)
  CmdAlt6 = `Cmd-Alt-6`, // block quote   (ctrl alt 6 / mod alt 6)
  CmdAlt7 = `Cmd-Alt-7`, // divider line  (ctrl alt 7 / mod alt 7)
  CmdAlt8 = `Cmd-Alt-8`, // code  (ctrl alt 8 / mod alt 8)
}

const CustomHeading = Heading.extend({
  // This is the same input rule that tiptap defines here: https://github.com/ueberdosis/tiptap/blob/main/packages/extension-heading/src/heading.ts#L95
  // but with a minor modification to the regex. We now use #{${level}} in the regex verus #{1,${level}} that tiptap uses.
  // Tiptap's regex means that `# ` would match with the level 2 regex (# - ##) and be reformatted as heading level 2.
  // Since we currently do not support heading level 1, we want `# ` to be completely ignored and left as is. This change allows that.
  addInputRules() {
    return this.options.levels.map((level) => {
      return textblockTypeInputRule({
        find: new RegExp(`^(#{${level}})\\s$`),
        type: this.type,
        getAttributes: {
          level,
        },
      });
    });
  },
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdAlt1]: () => false,
      [KEYBOARD_OVERRIDES.CmdAlt2]: () => this.editor.chain().focus().toggleHeading({level: 2}).run(),
      [KEYBOARD_OVERRIDES.CmdAlt3]: () => this.editor.chain().focus().toggleHeading({level: 3}).run(),
    };
  },
});

const CustomBulletList = BulletList.extend({
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdAlt4]: () => this.editor.commands.toggleBulletList(),
    };
  },
});

const CustomNumberedList = OrderedList.extend({
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdAlt5]: () => this.editor.commands.toggleOrderedList(),
    };
  },
});

const CustomBlockQuote = Blockquote.extend({
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdAlt6]: () => this.editor.commands.toggleBlockquote(),
    };
  },
});

const CustomDividerLine = HorizontalRule.extend({
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdAlt7]: () => this.editor.commands.setHorizontalRule(),
    };
  },
});

const CustomCode = Code.extend({
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdAlt8]: () => this.editor.commands.toggleCode(),
    };
  },
});

const CustomStrike = Strike.extend({
  addKeyboardShortcuts() {
    return {
      [KEYBOARD_OVERRIDES.CmdShiftS]: () => this.editor.commands.toggleStrike(),
    };
  },
});
export const extensions = ({
  placeholder,
  alwaysShowPlaceHolder,
}: {
  placeholder: string;
  alwaysShowPlaceHolder: boolean;
}) => [
  StarterKit.configure({
    // extensions to drop, because we're using a custom version of the extension
    blockquote: false,
    bulletList: false,
    heading: false,
    horizontalRule: false,
    orderedList: false,
    strike: false,
    code: false,
    history: false,
    // ignore for drag and drop
    dropcursor: false,
  }),
  StyledLink,
  Link,
  Underline,
  Highlight,
  TextAlign.configure({
    types: [TipTapFormats.Heading, TipTapFormats.Paragraph],
  }),
  Placeholder.configure({
    emptyEditorClass: alwaysShowPlaceHolder ? `always-show-placeholder` : `is-editor-empty`,
    placeholder,
  }),
  CustomBulletList,
  CustomHeading.configure({
    levels: [2, 3],
  }),
  CustomNumberedList,
  CustomBlockQuote,
  CustomDividerLine,
  CustomStrike,
  CustomCode,
];
